import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const freeFeatures = [
  { t: "Search & Award Calendar — next 30 days", ok: true },
  { t: "Explore availability by region", ok: true },
  { t: "Wishlist & shareable deal links", ok: true },
  { t: "AI travel assistant", ok: true },
  { t: "Full 1-year availability window", ok: false },
  { t: "Cabin Compare (Economy vs Business vs First)", ok: false },
  { t: "Alerts & price-drop notifications", ok: false },
  { t: "Points Wallet & live Flight Tracker", ok: false },
  { t: "Live real-time search", ok: false },
];
const PLANS = {
  monthly: { id: "monthly", key: "pro_monthly", title: "PRO Monthly", badge: "FLEXIBLE", price: "$8.99", per: "/mo", note: "Billed monthly · cancel anytime", cta: "Go PRO Monthly — $8.99/mo", fine: "$1 less than other competitors" },
  yearly: { id: "yearly", key: "pro_yearly", title: "PRO Yearly", badge: "BEST VALUE · 2 MONTHS FREE", price: "$89.90", per: "/yr", note: "12 months for the price of 10 — just $7.49/mo", cta: "Go PRO Yearly — $89.90/yr", fine: "Save $17.98 vs paying monthly" },
};
const proFeatures = [
  "Everything in Free",
  "Full year of award availability",
  "Cabin Compare — points, taxes & value side by side",
  "Alerts & price-drop notifications (in-app + email)",
  "Points Wallet — see which deals you can already afford",
  "Live Flight Tracker with delays & gates",
  "Live real-time search on any route",
  "Advanced filters (max fees, exclude carriers)",
  "Priority AI assistant",
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState("");

  const goPro = async (lookup_key) => {
    if (!user) {
      toast({ title: "Create an account first", description: "Sign up (it's free) to upgrade to PRO." });
      navigate("/register");
      return;
    }
    if (user.pro) {
      toast({ title: "You're already PRO", description: "Enjoy the full FindMyFlight experience!" });
      return;
    }
    setLoading(lookup_key);
    try {
      const { checkout_url } = await api.createCheckout({
        lookup_key,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = checkout_url;
    } catch (err) {
      toast({ title: "Could not start checkout", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
      setLoading("");
    }
  };

  const ProCard = ({ p, featured }) => (
    <div data-testid={`pro-card-${p.id}`} className={`relative rounded-3xl bg-white p-8 text-left ${featured ? "border-2 border-blue-600 shadow-xl shadow-blue-100" : "border border-slate-200"}`}>
      {p.badge && <span className={`absolute -top-3 left-8 rounded-full px-3 py-1 text-xs font-bold text-white ${featured ? "bg-blue-600" : "bg-slate-800"}`}>{p.badge}</span>}
      <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">{p.title}</p>
      <p className="mt-2 text-4xl font-extrabold text-slate-900" data-testid={`pro-price-${p.id}`}>{p.price}<span className="text-base font-medium text-slate-500">{p.per}</span></p>
      <p className="mt-1 text-xs font-semibold text-blue-600" data-testid={`pro-plan-note-${p.id}`}>{p.note}</p>
      <ul className="mt-6 space-y-3">
        {proFeatures.map((f) => (
          <li key={f} className="flex items-center gap-3 text-sm text-slate-700"><Check className="h-4 w-4 text-blue-600" /> {f}</li>
        ))}
      </ul>
      <Button onClick={() => goPro(p.key)} disabled={!!loading} data-testid={`go-pro-${p.id}`} className={`mt-8 w-full gap-2 font-semibold ${featured ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}>
        {loading === p.key ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to secure checkout…</> : p.cta}
      </Button>
      <p className="mt-3 text-center text-[11px] text-slate-400">{p.fine}</p>
    </div>
  );

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title="Pricing — Free forever, PRO $8.99/mo or $89.90/yr | FindMyFlight" description="FindMyFlight is free forever for essentials. Upgrade to PRO for $8.99/mo — a dollar less than other competitors — or $89.90/yr with 2 months free. Full year of availability, live search, SMS alerts and more." path="/pricing" />
      <div className="mx-auto max-w-6xl px-5 py-16 text-center">
        <h1 className="text-3xl font-extrabold text-slate-900 md:text-4xl">Simple, honest pricing</h1>
        <p className="mx-auto mt-3 max-w-xl text-slate-600">Start free, no registration required. Go PRO monthly or yearly when you’re ready for the full year and live search — both plans unlock the same features.</p>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 text-left">
            <p className="text-sm font-semibold uppercase tracking-wide text-green-600">Free</p>
            <p className="mt-2 text-4xl font-extrabold text-slate-900">$0<span className="text-base font-medium text-slate-500">/forever</span></p>
            <p className="mt-1 text-xs font-semibold text-green-600">No credit card, no registration</p>
            <ul className="mt-6 space-y-3">
              {freeFeatures.map((f) => (
                <li key={f.t} className="flex items-center gap-3 text-sm">
                  {f.ok ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-slate-300" />}
                  <span className={f.ok ? "text-slate-700" : "text-slate-400"}>{f.t}</span>
                </li>
              ))}
            </ul>
            <Link to="/search" className="mt-8 block"><Button variant="outline" className="w-full">Start searching free</Button></Link>
          </div>
          <ProCard p={PLANS.monthly} />
          <ProCard p={PLANS.yearly} featured />
        </div>
        <p className="mt-8 text-sm text-slate-500">Secure payment by Stripe · Cancel anytime · Promotion codes accepted at checkout</p>
      </div>
    </main>
  );
}
